import { useIonRouter } from '@ionic/react';
import { FirstAidIcon } from '@phosphor-icons/react';
import React, { useCallback, useMemo, useState } from 'react';

import { iconProps } from '../../App';
import CatPlaceholder from '../../assets/placeholders/cat.svg';
import DogPlaceholder from '../../assets/placeholders/dog.svg';
import {
  getRazas as axiosGetRazas,
  updatePetProfilePic as axiosUpdateProfilePic,
} from '../../axios/Pet';
import { ASSOC_PETS } from '../../axios/Utils/constants';
import axiosInstance from '../../axios/Utils/interceptors';
import PlanPaymentForm from '../../components/Pet/PlanPaymentForm';
import SiniestroForm from '../../components/Siniestro/SiniestroForm';
import FormHeader from '../../components/UI/FormHeader';
import UserForm from '../../components/User/UserForm';
import { usePets } from '../../context/PetsContext';
import { useUser } from '../../context/UserContext';
import { Pet, RazaRes, UpdatePicBody } from '../../types/Pet/types';
import { transformUser } from '../../types/User/types';
import {
  COVERAGE_EXPIRED_ALERT,
  hasExpiredCoverage,
  isDawerPet,
} from '../../utils/coverage';
import { canContractCoverage, getCoverageContractMissingFields } from '../../utils/user';
import useCamera from '../Utils/useCamera';
import { useCreateEntity, useFetchEntity } from '../Utils/useEntity';
import {
  DAWER_PHONE_CONSULTATION_UNAVAILABLE_ALERT,
  isDawerPhoneConsultationOpen,
  usePhoneConsultation,
} from '../Otros';
import { AlertOptions } from '../UI/useAlert';

/* ──────────────────────────────────────────────
 * Imagen de mascota
 * ────────────────────────────────────────────── */

const usePetPic = (pet: Pet | null) => {
  return useMemo(() => {
    if (!pet) return { petImage: DogPlaceholder, isCustomImage: false };

    // Cache la conversión de base64
    const cachedImage = pet.img_link
      ? `data:image/jpeg;base64,${pet.img_link}`
      : pet.animal?.toLowerCase() === 'perro'
        ? DogPlaceholder
        : CatPlaceholder;

    return {
      petImage: cachedImage,
      isCustomImage: Boolean(pet.img_link),
    };
  }, [pet]);
};

/* ──────────────────────────────────────────────
 * Actualizar foto de mascota
 * ────────────────────────────────────────────── */

const useNewPetProfilePic = () => {
  const { takePicture } = useCamera();
  const [isLoading, setIsLoading] = useState(false);

  const { create } = useCreateEntity<UpdatePicBody, void>(async ({ pic, petId }) => {
    await axiosUpdateProfilePic({ pic, petId });
  });

  const updatePetProfilePic = async (
    petId: string,
    sourceType: 'camera' | 'gallery',
    mutate: () => void,
  ) => {
    const dataUrl = await takePicture(sourceType);
    if (!dataUrl) return;

    setIsLoading(true);
    try {
      await create({ pic: dataUrl, petId });
      await mutate();
    } finally {
      setIsLoading(false);
    }
  };

  return { updatePetProfilePic, isLoading };
};

/* ──────────────────────────────────────────────
 * Razas
 * ────────────────────────────────────────────── */

const useRazas = () => {
  const { data, error, isValidating, mutate } = useFetchEntity<RazaRes[]>(
    'razas',
    axiosGetRazas,
  );

  const razas = {
    perro:
      data
        ?.filter((r) => r.animal === 'Perro')
        .map((r) => ({ value: r.nombre, label: r.nombre })) || [],
    gato:
      data
        ?.filter((r) => r.animal === 'Gato')
        .map((r) => ({ value: r.nombre, label: r.nombre })) || [],
  };

  return { razas, isValidating, error, mutate };
};

/* ──────────────────────────────────────────────
 * Sync de mascotas
 * ────────────────────────────────────────────── */

const useSyncPets = () => {
  const {
    create: syncPets,
    isLoading,
    error,
  } = useCreateEntity<string, void>(async (email: string) => {
    await axiosInstance.post(ASSOC_PETS, { email });
  });

  return { syncPets, isLoading, error };
};

/* ──────────────────────────────────────────────
 * Acciones de mascota
 * ────────────────────────────────────────────── */

interface PetActionsParams {
  pet: Pet | null;
  openModal: (content: React.ReactNode, options?: any) => void;
  closeModal: () => void;
  showAlert: (options: AlertOptions) => void;
}

const usePetActions = ({ pet, openModal, closeModal, showAlert }: PetActionsParams) => {
  const { push } = useIonRouter();
  const { datosExtra } = usePets();
  const { user } = useUser();
  const coverageExpired = hasExpiredCoverage(pet);
  const isDawer = isDawerPet(pet);
  const { userData, addressData } = useMemo(() => transformUser(user), [user]);
  const phoneConsultationLink = isDawer
    ? datosExtra?.telefonia?.celTigre ?? datosExtra?.telefonia?.link
    : datosExtra?.telefonia?.link;

  const { openPhoneConsultation } = usePhoneConsultation({
    phoneLink: phoneConsultationLink,
    showAlert,
    canOpen: isDawer ? isDawerPhoneConsultationOpen : undefined,
    unavailableAlert: DAWER_PHONE_CONSULTATION_UNAVAILABLE_ALERT,
  });

  const openCoverageRequiredAlert = useCallback(() => {
    showAlert({
      ...COVERAGE_EXPIRED_ALERT,
      buttons: [{ text: 'Entendido' }],
    });
  }, [showAlert]);

  const openNuevoSiniestro = useCallback(() => {
    if (!pet) return;
    if (coverageExpired) {
      openCoverageRequiredAlert();
      return;
    }

    openModal(
      <SiniestroForm
        header={
          <FormHeader
            icon={<FirstAidIcon {...iconProps} />}
            title="Nuevo caso"
            subtitle="Contanos qué pasó para ayudarte"
          />
        }
        pet={pet}
        closeModal={closeModal}
      />,
    );
  }, [pet, coverageExpired, openCoverageRequiredAlert, openModal, closeModal]);

  const openHistorial = useCallback(() => {
    if (coverageExpired) {
      openCoverageRequiredAlert();
      return;
    }

    push('/tabs/pet/siniestros');
  }, [coverageExpired, openCoverageRequiredAlert, push]);

  const openTelemedicina = useCallback(() => {
    if (coverageExpired) {
      openCoverageRequiredAlert();
      return;
    }

    openPhoneConsultation();
  }, [coverageExpired, openCoverageRequiredAlert, openPhoneConsultation]);

  const openPlanPayment = useCallback(() => {
    if (!pet) return;

    if (!canContractCoverage(user)) {
      const missingFields = getCoverageContractMissingFields(user);

      console.warn('[CoverageContract] Perfil incompleto para contratar cobertura', {
        missingFields,
        user,
      });

      showAlert({
        header: 'Completá tus datos',
        message: `Para contratar una cobertura necesitás completar: ${missingFields.join(', ')}.`,
        buttons: [
          { text: 'Cancelar', role: 'cancel' },
          {
            text: 'Completar datos',
            handler: () =>
              openModal(
                <UserForm
                  closeModal={closeModal}
                  userData={userData}
                  addressData={addressData}
                  isRequired
                  submitMode="direct"
                />,
                { navigation: false },
              ),
          },
        ],
      });
      return;
    }

    openModal(<PlanPaymentForm pet={pet} closeModal={closeModal} />, {
      navigation: false,
    });
  }, [pet, user, showAlert, openModal, closeModal, userData, addressData]);

  return {
    openNuevoSiniestro,
    openHistorial,
    openTelemedicina,
    openPlanPayment,
    openCoverageRequiredAlert,
  };
};

export { usePetPic, useNewPetProfilePic, useRazas, useSyncPets, usePetActions };
